import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Steps, Typography } from 'antd';
import { WhatsAppOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import futezapLogo from '../assets/futezap.jpg';

const { Title, Text } = Typography;

const ForgotPassword = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [teamName, setTeamName] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const steps = [
    {
      title: 'WhatsApp',
      icon: <WhatsAppOutlined />
    },
    {
      title: 'Código',
      icon: <SafetyOutlined />
    },
    {
      title: 'Nova Senha',
      icon: <SafetyOutlined />
    }
  ];

  const handleRequestCode = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/password-reset/request-code', {
        whatsapp: values.whatsapp
      });
      
      if (response.data.success) {
        setWhatsapp(values.whatsapp);
        setCurrentStep(1);
        message.success('Código enviado via WhatsApp!');
      } else {
        if (response.data.field === 'whatsapp') {
          form.setFields([{
            name: 'whatsapp',
            errors: [response.data.message]
          }]);
        } else {
          message.error(response.data.message || 'Erro ao enviar código');
        }
      }
    } catch (error) {
      if (error.response?.status === 404 && error.response?.data?.field === 'whatsapp') {
        form.setFields([{
          name: 'whatsapp',
          errors: [error.response.data.message]
        }]);
      } else {
        message.error('Erro ao solicitar código');
      }
    }
    setLoading(false);
  };

  const handleVerifyCode = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/password-reset/verify-code', {
        whatsapp: whatsapp,
        code: values.code
      });
      
      if (response.data.success) {
        setResetToken(response.data.token);
        setTeamName(response.data.team_name);
        setCurrentStep(2);
        message.success('Código válido! Defina sua nova senha.');
      } else {
        message.error(response.data.message || 'Código inválido');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Código inválido ou expirado');
    }
    setLoading(false);
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/password-reset/reset-password', {
        token: resetToken,
        password: values.password
      });
      
      if (response.data.success) {
        message.success('Senha redefinida com sucesso!');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        message.error(response.data.message || 'Erro ao redefinir senha');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Erro ao redefinir senha');
    }
    setLoading(false);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #00B96B 0%, #52c41a 100%)', 
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Card style={{ 
        width: '100%', 
        maxWidth: 400, 
        borderRadius: 12, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)' 
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={futezapLogo} alt="Futezap" style={{ height: 60, borderRadius: 8, marginBottom: 16 }} />
          <Title level={3} style={{ color: '#00B96B', marginBottom: 8 }}>
            {currentStep === 2 ? 'Nova Senha' : 'Recuperar Senha'}
          </Title>
          <Text style={{ color: '#666' }}>
            {currentStep === 0 ? 
              'Digite seu WhatsApp para receber o código' : 
              currentStep === 1 ?
              'Digite o código enviado no WhatsApp' :
              `Time: ${teamName}`
            }
          </Text>
        </div>

        <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />

        {currentStep === 0 && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleRequestCode}
          >
            <Form.Item
              name="whatsapp"
              label="WhatsApp (DDD + Número)"
              rules={[
                { required: true, message: 'WhatsApp é obrigatório' },
                { pattern: /^\d{10,11}$/, message: 'Digite apenas números (DDD + número)' }
              ]}
              validateStatus={form.getFieldError('whatsapp').length > 0 ? 'error' : ''}
              help={form.getFieldError('whatsapp')[0]}
            >
              <Input 
                placeholder="11999887766" 
                size="large"
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                loading={loading}
                block
              >
                Enviar Código
              </Button>
            </Form.Item>
          </Form>
        )}

        {currentStep === 1 && (
          <Form
            layout="vertical"
            onFinish={handleVerifyCode}
          >
            <Form.Item
              name="code"
              label="Código de Verificação"
              rules={[
                { required: true, message: 'Código é obrigatório' },
                { len: 6, message: 'Código deve ter 6 dígitos' }
              ]}
            >
              <Input 
                placeholder="123456" 
                size="large"
                maxLength={6}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                loading={loading}
                block
              >
                Verificar Código
              </Button>
            </Form.Item>

            <Form.Item>
              <Button 
                type="text" 
                onClick={() => setCurrentStep(0)}
                block
              >
                Voltar
              </Button>
            </Form.Item>
          </Form>
        )}

        {currentStep === 2 && (
          <Form
            layout="vertical"
            onFinish={handleResetPassword}
          >
            <Form.Item
              name="password"
              label="Nova Senha"
              rules={[
                { required: true, message: 'Senha é obrigatória' },
                { min: 6, message: 'Senha deve ter pelo menos 6 caracteres' }
              ]}
            >
              <Input.Password 
                placeholder="Digite sua nova senha" 
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirmar Senha"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Confirmação de senha é obrigatória' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('As senhas não coincidem'));
                  },
                }),
              ]}
            >
              <Input.Password 
                placeholder="Confirme sua nova senha" 
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                size="large" 
                loading={loading}
                block
              >
                Redefinir Senha
              </Button>
            </Form.Item>

            <Form.Item>
              <Button 
                type="text" 
                onClick={() => setCurrentStep(1)}
                block
              >
                Voltar
              </Button>
            </Form.Item>
          </Form>
        )}

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button type="link" onClick={() => navigate('/login')}>
            Voltar ao Login
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPassword;